import React, { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import CrossIcon from '@atlaskit/icon/core/cross';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import DeleteIcon from '@atlaskit/icon/core/delete';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	ARCHIVE_PAGES_EXPERIENCE,
	BULK_DELETE_PAGES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { BulkDeletePagesActionLoader } from '@confluence/bulk-page-actions/entry-points/BulkDeletePagesAction.ts';
import { BULK_DELETE_PAGE_SELECTION_LIMIT_DEFAULT } from '@confluence/bulk-page-actions/entry-points/constants.ts';
import { BulkArchivePagesLoader } from '@confluence/archive-pages/entry-points/BulkArchivePagesLoader.ts';
import { BULK_ARCHIVE_PAGE_SELECTION_LIMIT } from '@confluence/archive-pages/entry-points/archivingConstants.ts';
import { useContentTreeSelection } from '@confluence/content-tree-actions/entry-points/store.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { usePrimaryButtons } from './usePrimaryButtons.ts';
// eslint-disable-next-line import/no-cycle
import { BulkActionsButton } from './BulkActionsButton.tsx';

export const BULK_ACTIONS_ANALYTICS_SOURCE = 'contentTreeBulkActions';
const BULK_ACTION_SUBJECT_ID = 'bulkAction';
export const CLEAR_SELECTION_ACTION_NAME = 'clearSelection';
export const BULK_ARCHIVE_ACTION_NAME = 'bulkArchive';
export const BULK_DELETE_ACTION_NAME = 'bulkDelete';
export const EXIT_ACTION_NAME = 'exit';

export const BulkActionsMenu = (): React.JSX.Element => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [{ count, loadingDescendantIds }, { clearSelection, exitMultiSelect }] =
		useContentTreeSelection();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const isDeleteLimitReached = count > BULK_DELETE_PAGE_SELECTION_LIMIT_DEFAULT;
	const isArchiveLimitReached = count > BULK_ARCHIVE_PAGE_SELECTION_LIMIT;

	const { getPrimaryActionInput, getCurrentSpace } = usePrimaryButtons();
	const { spaceKey, spaceId } = getCurrentSpace();

	const isDescendantCountLoading = loadingDescendantIds.size > 0;

	const handleKeydown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && !isModalOpen) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: BULK_ACTIONS_ANALYTICS_SOURCE,
						action: 'closed',
						actionSubject: 'bulkActionsMenu',
						attributes: {
							method: 'escapeKey',
						},
					},
				}).fire();
				exitMultiSelect();
			}
		},
		[createAnalyticsEvent, exitMultiSelect, isModalOpen],
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeydown);
		return () => {
			document.removeEventListener('keydown', handleKeydown);
		};
	}, [handleKeydown]);

	const handleArchiveClick = (dialogs: DialogsStateContainer, flags: FlagsStateContainer) => {
		setIsModalOpen(true);
		const primaryActionInput = getPrimaryActionInput();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: BULK_ACTIONS_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: BULK_ACTION_SUBJECT_ID,
				attributes: { action: BULK_ARCHIVE_ACTION_NAME, count, spaceKey },
			},
		}).fire();
		experienceTracker.start({
			name: ARCHIVE_PAGES_EXPERIENCE,
			attributes: {
				featureName: BULK_ACTIONS_ANALYTICS_SOURCE,
			},
		});
		if (primaryActionInput instanceof Error) {
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.flagErrorTitle),
				description: intl.formatMessage(i18n.flagErrorDescription),
			});
		} else {
			dialogs.showModal(BulkArchivePagesLoader, {
				mutationOutputList: primaryActionInput,
				expectedPageCount: count,
				spaceKey: spaceKey ?? '',
				spaceId,
				onSuccess: () => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'bulkArchive',
							action: 'completed',
							actionSubject: BULK_ACTION_SUBJECT_ID,
							attributes: { action: BULK_ARCHIVE_ACTION_NAME, count, spaceKey },
						},
					}).fire();
					exitMultiSelect();
					clearSelection();
					setIsModalOpen(false);
				},
				onClose: () => {
					setIsModalOpen(false);
				},
				featureName: BULK_ACTIONS_ANALYTICS_SOURCE,
			});
		}
	};

	const handleDeleteClick = (dialogs: DialogsStateContainer, flags: FlagsStateContainer) => {
		setIsModalOpen(true);
		const primaryActionInput = getPrimaryActionInput();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: BULK_ACTIONS_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: BULK_ACTION_SUBJECT_ID,
				attributes: { action: BULK_DELETE_ACTION_NAME, count, spaceKey },
			},
		}).fire();
		experienceTracker.start({
			name: BULK_DELETE_PAGES_EXPERIENCE,
			attributes: {
				featureName: BULK_ACTIONS_ANALYTICS_SOURCE,
			},
		});
		if (primaryActionInput instanceof Error) {
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.flagErrorTitle),
				description: intl.formatMessage(i18n.flagErrorDescription),
			});
		} else {
			dialogs.showModal(BulkDeletePagesActionLoader, {
				mutationOutputList: primaryActionInput,
				expectedPageCount: count,
				spaceKey: spaceKey ?? '',
				spaceId,
				source: BULK_ACTIONS_ANALYTICS_SOURCE,
				onSuccess: () => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'bulkDelete',
							action: 'completed',
							actionSubject: BULK_ACTION_SUBJECT_ID,
							attributes: { action: BULK_DELETE_ACTION_NAME, count, spaceKey },
						},
					}).fire();
					window.location.reload();
					exitMultiSelect();
					setIsModalOpen(false);
				},
				onClose: () => {
					setIsModalOpen(false);
				},
				featureName: BULK_ACTIONS_ANALYTICS_SOURCE,
			});
		}
	};

	const handleExitClick = () => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'closed',
				actionSubject: 'bulkActionsMenu',
				attributes: {
					method: 'exitActionButton',
				},
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: BULK_ACTIONS_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: BULK_ACTION_SUBJECT_ID,
				attributes: { action: EXIT_ACTION_NAME, spaceKey },
			},
		}).fire();
		exitMultiSelect();
	};

	const countButton = (
		<BulkActionsButton
			actionText={i18n.selected}
			actionIcon={<CrossIcon label="" />}
			actionName={CLEAR_SELECTION_ACTION_NAME}
			count={count}
			onClick={clearSelection}
			isLoading={isDescendantCountLoading}
		/>
	);

	return (
		<Subscribe to={[DialogsStateContainer, FlagsStateContainer]}>
			{(dialogs: DialogsStateContainer, flags: FlagsStateContainer) => (
				<Stack
					space="space.100"
					alignBlock="start"
					xcss={menuStyles}
					role="toolbar"
					aria-label={intl.formatMessage(i18n.bulkActionsLabel)}
				>
					{/* Screen reader announcer */}
					<VisuallyHidden role="status" aria-live="assertive" aria-atomic="true">
						{intl.formatMessage(i18n.announceMenuOpen)}
					</VisuallyHidden>

					{isDescendantCountLoading ? (
						<Tooltip content={intl.formatMessage(i18n.loadingTooltip)} position="right">
							{countButton}
						</Tooltip>
					) : (
						countButton
					)}

					<BulkActionsButton
						actionText={i18n.archive}
						actionIcon={<ArchiveBoxIcon label="" />}
						actionName={BULK_ARCHIVE_ACTION_NAME}
						count={count}
						onClick={() => handleArchiveClick(dialogs, flags)}
						isSelectionLimitReached={isArchiveLimitReached}
						actionLimit={BULK_ARCHIVE_PAGE_SELECTION_LIMIT}
						isDisabled={isModalOpen}
					/>
					<BulkActionsButton
						actionText={i18n.delete}
						actionIcon={<DeleteIcon label="" />}
						actionName={BULK_DELETE_ACTION_NAME}
						count={count}
						onClick={() => handleDeleteClick(dialogs, flags)}
						isSelectionLimitReached={isDeleteLimitReached}
						actionLimit={BULK_DELETE_PAGE_SELECTION_LIMIT_DEFAULT}
						isDisabled={isModalOpen}
					/>

					<Box xcss={exitButtonStyle}>
						<BulkActionsButton
							actionText={i18n.exit}
							actionName={EXIT_ACTION_NAME}
							count={count}
							onClick={handleExitClick}
						/>
					</Box>
				</Stack>
			)}
		</Subscribe>
	);
};
const i18n = defineMessages({
	selected: {
		id: 'page-tree.bulk-actions-menu.selected',
		defaultMessage: '{count, plural, one {# item} other {# items}} selected',
		description: 'The label displaying the number of items selected in the bulk actions menu',
	},
	archive: {
		id: 'page-tree.bulk-actions-menu.archive',
		defaultMessage: 'Archive',
		description: 'The label for the archive action in the bulk actions menu',
	},
	delete: {
		id: 'page-tree.bulk-actions-menu.delete',
		defaultMessage: 'Delete',
		description: 'The label for the delete action in the bulk actions menu',
	},
	exit: {
		id: 'page-tree.bulk-actions-menu.exit',
		defaultMessage: 'Exit',
		description: 'The label for the exit action in the bulk actions menu',
	},
	bulkActionsLabel: {
		id: 'page-tree.bulk-actions-menu.label',
		defaultMessage: 'Bulk actions menu',
		description:
			'The text is used as the accessible aria-label for the bulk actions dropdown menu in the page tree, which appears when one or more pages are selected.',
	},
	announceMenuOpen: {
		id: 'page-tree.bulk-actions-menu.announce-menu-open',
		defaultMessage: 'Bulk actions menu for the content tree is open.',
		description: 'Screen reader text to announce that the bulk actions menu is open',
	},
	loadingTooltip: {
		id: 'page-tree.bulk-actions-menu.loading-tooltip',
		defaultMessage:
			'We’re still processing your selection. This may take longer depending on the amount of content.',
		description: 'Tooltip text shown when the bulk actions menu is loading',
	},
	flagErrorTitle: {
		id: 'page-tree.bulk-actions-menu.error.title',
		defaultMessage: 'Something went wrong',
		description: 'Title of the error flag when bulk actions fail',
	},
	flagErrorDescription: {
		id: 'page-tree.bulk-actions-menu.error.description',
		defaultMessage:
			'Refresh and try again. If this keeps happening, contact your space administrator.',
		description:
			'A generic error message that displays in a flag to let the user know that there was a problem while loading their tree',
	},
});

const menuStyles = xcss({
	position: 'fixed',
	top: 'calc(316px + var(--topNavigationHeight, 0px))',
	left: 'calc(9px + var(--leftSidebarWidth, 0px))',
});

const exitButtonStyle = xcss({
	paddingTop: 'space.100',
});
